import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import {
    ConnectionState,
    DisconnectReason,
    Room,
    RoomEvent,
    Track,
    createLocalTracks,
    createLocalScreenTracks,
} from 'livekit-client'
import { chatmixCallsService } from '@/services/chatmixCalls'
import { useChatAttendance } from '@/stores/chat/attendances'

let room = null
let localTracks = []
let screenTrack = null
let pendingScreenToggle = null
let agentToken = null
let livekitUrl = null
let needsTokenRefresh = false

const DISCONNECT_MESSAGES = {
    [DisconnectReason.DUPLICATE_IDENTITY]: 'Você entrou nesta chamada em outra aba ou dispositivo.',
    [DisconnectReason.SERVER_SHUTDOWN]: 'O servidor de mídia foi reiniciado.',
    [DisconnectReason.PARTICIPANT_REMOVED]: 'Você foi removido da chamada.',
    [DisconnectReason.ROOM_DELETED]: 'A sala desta chamada não existe mais no servidor de mídia.',
    [DisconnectReason.ROOM_CLOSED]: 'A chamada foi encerrada.',
    [DisconnectReason.JOIN_FAILURE]: 'Não foi possível entrar na sala de mídia.',
    [DisconnectReason.SIGNAL_CLOSE]: 'A conexão com o servidor de mídia caiu.',
    [DisconnectReason.CONNECTION_TIMEOUT]: 'Tempo esgotado ao conectar no servidor de mídia.',
    [DisconnectReason.MEDIA_FAILURE]: 'Falha na transmissão de áudio/vídeo.',
}

const TERMINAL_DISCONNECTS = [
    DisconnectReason.ROOM_DELETED,
    DisconnectReason.ROOM_CLOSED,
    DisconnectReason.PARTICIPANT_REMOVED,
]

const POLL_INTERVAL_MS = 3000
const TERMINAL_SERVER_STATUS = ['ended', 'missed', 'recording_ready']

export const useChatmixCallsStore = defineStore('chatmixCalls', {
    state: () => ({
        status: 'idle', // idle | creating | waiting | connecting | active | ended
        serverStatus: null,
        callType: 'video',
        callId: null,
        joinUrl: '',
        duration: null,
        recordingUrl: null,
        micMuted: false,
        screenSharing: false,
        error: null,
        reconnecting: false,
        clientJoined: false,
        remoteTracks: [],
        callStartedAt: null,
        _timerInterval: null,
        _pollInterval: null,
    }),

    getters: {
        hasActiveCall: (state) => ['creating', 'waiting', 'connecting', 'active'].includes(state.status),
        isInCall: (state) => state.status === 'active',
        isWaiting: (state) => state.status === 'waiting',
        isConnecting: (state) => state.status === 'connecting',
        isCreating: (state) => state.status === 'creating',
        remoteScreenSharing: (state) => state.remoteTracks.some((track) => track.source === Track.Source.ScreenShare),
        showingScreenShare: (state) => state.screenSharing
            || state.remoteTracks.some((track) => track.source === Track.Source.ScreenShare),
    },

    actions: {
        async startCall(callType = 'video') {
            if (this.hasActiveCall) {
                console.warn('[ChatmixCalls] Já existe uma chamada em andamento')
                return
            }

            const attendance = useChatAttendance()().currentAttendance
            if (!attendance?.id) {
                this.error = 'Nenhum atendimento selecionado'
                return
            }

            this.status = 'creating'
            this.callType = callType
            this.error = null

            try {
                const data = await chatmixCallsService.createCall(attendance.id, callType)
                this.callId = data?.call_id || null
                this.joinUrl = data?.join_url || ''
                agentToken = data?.agent_token || null
                livekitUrl = data?.livekit_url || null
                needsTokenRefresh = false

                if (!this.callId || !agentToken || !livekitUrl) {
                    throw new Error('Resposta inválida ao criar chamada')
                }

                this.serverStatus = data?.status || 'created'
                this.status = 'waiting'
            } catch (e) {
                console.error('[ChatmixCalls] Falha ao criar chamada:', e)
                this.error = e?.message || 'Falha ao criar chamada'
                this.status = 'idle'
                return
            }

            this._startPolling()
            await this.joinCall()
        },

        async joinCall() {
            if (this.status === 'connecting' || this.status === 'active') return
            if (!this.callId || !agentToken || !livekitUrl) {
                this.error = 'Credenciais da chamada indisponíveis. Inicie a chamada novamente.'
                return
            }

            this.status = 'connecting'
            this.error = null
            this.reconnecting = false
            this.clientJoined = false
            this.screenSharing = false
            this.remoteTracks = []

            if (needsTokenRefresh) {
                const fresh = await this.refreshToken()
                if (!fresh) {
                    if (this.status === 'connecting') this.status = 'waiting'
                    this.error = this.error || 'Não foi possível reemitir o token da chamada.'
                    return
                }
                needsTokenRefresh = false
            }

            try {
                localTracks = await createLocalTracks({
                    audio: true,
                    video: this.callType !== 'audio',
                })
            } catch (e) {
                console.error('[ChatmixCalls] Falha ao acessar câmera/microfone:', e)
                this.error = 'Permita o acesso à câmera e ao microfone para entrar na chamada.'
                this.status = 'waiting'
                localTracks = []
                return
            }

            const nextRoom = markRaw(new Room({ adaptiveStream: true, dynacast: true }))
            room = nextRoom

            nextRoom.on(RoomEvent.TrackSubscribed, (track) => {
                this.remoteTracks.push(markRaw(track))
            })

            nextRoom.on(RoomEvent.TrackUnsubscribed, (track) => {
                try { track.detach() } catch { /* já solta */ }
                this.remoteTracks = this.remoteTracks.filter((t) => t !== track)
            })

            const syncClientJoined = () => {
                if (room !== nextRoom) return
                this.clientJoined = nextRoom.remoteParticipants.size > 0
            }

            nextRoom.on(RoomEvent.ParticipantConnected, syncClientJoined)
            nextRoom.on(RoomEvent.ParticipantDisconnected, syncClientJoined)

            nextRoom.on(RoomEvent.Reconnecting, () => {
                if (room !== nextRoom) return
                this.reconnecting = true
            })

            nextRoom.on(RoomEvent.Reconnected, () => {
                if (room !== nextRoom) return
                this.reconnecting = false
            })

            nextRoom.on(RoomEvent.Disconnected, (reason) => {
                // Ignora eventos de salas antigas (ex.: double-join), para não
                // desconectar a sala atual.
                if (room !== nextRoom) return

                this._stopTimer()
                this._handleDisconnect(reason)
                this._disposeRoom({ keepCredentials: true })
            })

            try {
                await nextRoom.connect(livekitUrl, agentToken)
                if (room !== nextRoom) return

                for (const track of localTracks) {
                    await nextRoom.localParticipant.publishTrack(track)
                    if (room !== nextRoom) return
                }

                // publishTrack não rejeita quando a negociação encontra um peer
                // connection já fechado, então confirmamos o estado da sala.
                if (nextRoom.state !== ConnectionState.Connected) {
                    throw new Error('A conexão com o servidor de mídia caiu durante a entrada.')
                }

                syncClientJoined()
                this.micMuted = false
                this.status = 'active'
                this._startTimer()
            } catch (e) {
                console.error('[ChatmixCalls] Falha ao entrar na chamada:', e)
                if (room !== nextRoom) return
                needsTokenRefresh = true
                this.error = e?.message || 'Falha ao entrar na chamada'
                this.status = 'waiting'
                await this._disposeRoom({ keepCredentials: true })
            }
        },

        _handleDisconnect(reason) {
            if (this.status !== 'active' && this.status !== 'connecting') return

            if (reason == null || reason === DisconnectReason.CLIENT_INITIATED) {
                this.status = 'ended'
                return
            }

            console.warn('[ChatmixCalls] Desconectado da sala:', DisconnectReason[reason] ?? reason)
            this.error = DISCONNECT_MESSAGES[reason] || 'A chamada foi interrompida.'

            if (TERMINAL_DISCONNECTS.includes(reason)) {
                this.status = 'ended'
                return
            }

            needsTokenRefresh = true
            this.status = 'waiting'
        },

        _getLocalScreenTrack() {
            return screenTrack
        },

        attachMedia(localVideoEl, mainVideoEl) {
            const localCamera = localTracks.find(
                (track) => track.kind === Track.Kind.Video && track.source !== Track.Source.ScreenShare,
            )
            if (localCamera && localVideoEl && !localCamera.attachedElements.includes(localVideoEl)) {
                localCamera.attach(localVideoEl)
            }

            const remoteVideos = this.remoteTracks.filter((track) => track.kind === Track.Kind.Video)
            const remoteScreen = remoteVideos.find((track) => track.source === Track.Source.ScreenShare)
            const remoteCamera = remoteVideos.find((track) => track.source !== Track.Source.ScreenShare)
            const mainTrack = remoteScreen || remoteCamera || null
            const localScreen = this._getLocalScreenTrack()

            for (const track of [...remoteVideos, localScreen].filter(Boolean)) {
                if (track !== mainTrack && mainVideoEl && track.attachedElements.includes(mainVideoEl)) {
                    track.detach(mainVideoEl)
                }
            }

            if (mainTrack && mainVideoEl && !mainTrack.attachedElements.includes(mainVideoEl)) {
                mainTrack.attach(mainVideoEl)
            }

            for (const track of this.remoteTracks) {
                if (track.kind !== Track.Kind.Video && track.attachedElements.length === 0) {
                    track.attach()
                }
            }
        },

        toggleMute() {
            this.micMuted = !this.micMuted
            for (const track of localTracks) {
                if (track.kind === Track.Kind.Audio) {
                    if (this.micMuted) track.mute()
                    else track.unmute()
                }
            }
        },

        async toggleScreenShare() {
            if (!room || this.status !== 'active') return
            if (pendingScreenToggle) await pendingScreenToggle.catch(() => {})

            const current = (async () => {
                if (this.screenSharing) {
                    await this._stopScreenShare()
                    return
                }

                const tracks = await createLocalScreenTracks({ audio: false })
                const track = tracks[0]
                if (!track) return

                track.mediaStreamTrack.addEventListener('ended', () => {
                    if (screenTrack === track) this._stopScreenShare()
                })

                try {
                    await room.localParticipant.publishTrack(track)
                    screenTrack = markRaw(track)
                    this.screenSharing = true
                } catch (err) {
                    track.stop()
                    throw err
                }
            })()

            pendingScreenToggle = current
            try {
                await current
            } catch (e) {
                this.screenSharing = false
                if (e?.name === 'NotAllowedError' || e?.name === 'AbortError') return
                console.error('[ChatmixCalls] Falha ao compartilhar tela:', e)
            } finally {
                if (pendingScreenToggle === current) pendingScreenToggle = null
            }
        },

        async _stopScreenShare() {
            const track = screenTrack
            screenTrack = null
            this.screenSharing = false
            if (!track) return
            if (room) {
                try { await room.localParticipant.unpublishTrack(track) } catch { /* já removida */ }
            }
            try { track.stop() } catch { /* já parado */ }
        },

        async sendAnnotation(message) {
            if (!room || this.status !== 'active') return
            const bytes = new Uint8Array(new TextEncoder().encode(JSON.stringify(message)))
            try {
                await room.localParticipant.publishData(bytes, {
                    reliable: true,
                    topic: 'annotation',
                })
            } catch (e) {
                console.error('[ChatmixCalls] Falha ao enviar anotação:', e)
            }
        },

        async endCall() {
            this._stopTimer()
            this._stopPolling()

            if (this.callId) {
                try {
                    await chatmixCallsService.endCall(this.callId)
                } catch (e) {
                    console.error('[ChatmixCalls] Falha ao encerrar chamada:', e)
                }
            }

            this.status = 'ended'
            await this._disposeRoom()
        },

        async refreshToken() {
            if (!this.callId) return

            try {
                const data = await chatmixCallsService.getAgentToken(this.callId)
                if (data?.agent_token) {
                    agentToken = data.agent_token
                    return agentToken
                }
                return null
            } catch (e) {
                if (e?.response?.status === 410) {
                    console.warn('[ChatmixCalls] Chamada não está mais "entrável" (410)')
                    this.error = 'Esta chamada não está mais disponível.'
                    this.status = 'ended'
                }
                return null
            }
        },

        reset() {
            this._stopTimer()
            this._stopPolling()
            this._disposeRoom()
            needsTokenRefresh = false
            this.status = 'idle'
            this.serverStatus = null
            this.callType = 'video'
            this.callId = null
            this.joinUrl = ''
            this.duration = null
            this.recordingUrl = null
            this.micMuted = false
            this.screenSharing = false
            this.error = null
            this.reconnecting = false
            this.clientJoined = false
            this.remoteTracks = []
            this.callStartedAt = null
        },

        _startPolling() {
            this._stopPolling()
            this._pollInterval = setInterval(() => this._pollStatus(), POLL_INTERVAL_MS)
        },

        _stopPolling() {
            if (this._pollInterval) {
                clearInterval(this._pollInterval)
                this._pollInterval = null
            }
        },

        async _pollStatus() {
            if (!this.callId) return

            let data
            try {
                data = await chatmixCallsService.getCallStatus(this.callId)
            } catch {
                return
            }
            if (!data) return

            this.serverStatus = data.status ?? this.serverStatus
            if (data.recording_url) this.recordingUrl = data.recording_url

            if (typeof data.duration === 'number') {
                this.duration = data.duration
                // Reancora o timer local para ele interpolar em fase com o
                // servidor entre uma consulta e outra, sem saltos visíveis.
                this.callStartedAt = Date.now() - data.duration * 1000
            }

            if (TERMINAL_SERVER_STATUS.includes(this.serverStatus)) {
                this._stopPolling()
                this._stopTimer()
                if (this.hasActiveCall) {
                    this.status = 'ended'
                    this._disposeRoom()
                }
            }
        },

        _startTimer() {
            this._stopTimer()
            this.callStartedAt = Date.now()
            this.duration = 0
            this._timerInterval = setInterval(() => {
                this.duration = Math.floor((Date.now() - this.callStartedAt) / 1000)
            }, 1000)
        },

        _stopTimer() {
            if (this._timerInterval) {
                clearInterval(this._timerInterval)
                this._timerInterval = null
            }
        },

        async _disposeRoom({ keepCredentials = false } = {}) {
            const closingRoom = room
            const closingTracks = localTracks
            const closingScreen = screenTrack
            room = null
            localTracks = []
            screenTrack = null

            if (!keepCredentials) {
                agentToken = null
                livekitUrl = null
            }
            this.reconnecting = false
            this.clientJoined = false
            this.screenSharing = false
            this.remoteTracks = []

            if (closingScreen) {
                try { await closingRoom?.localParticipant.unpublishTrack(closingScreen) } catch { /* já removida */ }
                try { closingScreen.stop() } catch { /* já parado */ }
            }

            if (closingRoom) {
                try { await closingRoom.disconnect() } catch { /* já desconectado */ }
            }

            for (const track of closingTracks) {
                try { track.stop() } catch { /* já parado */ }
            }
        },
    },
})
